/**
 * EvaluationWizard — 6-step final evaluation, shown when all 8 weeks
 * have been passed. Mirrors the BenchmarkWizard shape with the same
 * WizardShell + SliderRating + TextAnswer primitives so the two flows
 * feel identical.
 *
 * Questions (matching the backend's ``_EVALUATION_REQUIRED_FIELDS``)
 * ---------
 *  1. Self-rating technical        (slider 1–5)
 *  2. Self-rating communication    (slider 1–5)
 *  3. Self-rating leadership       (slider 1–5)
 *  4. Highlights                   (text)
 *  5. Challenges                   (text)
 *  6. Next 12 months               (text)
 */

import { useCallback, useState, type ReactNode } from 'react';
import {
    HeartHandshake,
    Mic,
    Sparkles,
    Star,
    Target,
    Wrench,
} from 'lucide-react';
import Card from '../ui/Card';
import SliderRating from './wizard/SliderRating';
import TextAnswer from './wizard/TextAnswer';
import WizardShell from './wizard/WizardShell';
import type { EvaluationPayload } from '../../bootcampService';
import './BenchmarkWizard.css'; // reuse the generating-roadmap overlay styles

type Props = {
    onSubmit: (payload: EvaluationPayload) => Promise<void>;
};

type StepKey =
    | 'rate_technical'
    | 'rate_communication'
    | 'rate_leadership'
    | 'highlights'
    | 'challenges'
    | 'next_12_months';

const STEP_ORDER: StepKey[] = [
    'rate_technical',
    'rate_communication',
    'rate_leadership',
    'highlights',
    'challenges',
    'next_12_months',
];
const TOTAL_STEPS = STEP_ORDER.length;

function EvaluationWizard({ onSubmit }: Props) {
    const [ratingTechnical, setRatingTechnical] = useState(3);
    const [ratingCommunication, setRatingCommunication] = useState(3);
    const [ratingLeadership, setRatingLeadership] = useState(3);
    const [highlights, setHighlights] = useState('');
    const [challenges, setChallenges] = useState('');
    const [nextTwelveMonths, setNextTwelveMonths] = useState('');
    const [stepIdx, setStepIdx] = useState(0);
    const [saving, setSaving] = useState(false);

    const currentKey = STEP_ORDER[stepIdx];

    const canContinue = (() => {
        switch (currentKey) {
            case 'rate_technical':
            case 'rate_communication':
            case 'rate_leadership':
                return true;
            case 'highlights': return highlights.trim().length >= 20;
            case 'challenges': return challenges.trim().length >= 20;
            case 'next_12_months': return nextTwelveMonths.trim().length >= 20;
            default: return false;
        }
    })();

    const handleBack = useCallback(() => {
        setStepIdx((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleExit = useCallback(() => {
        // No-op — the parent doesn't offer an exit path out of the
        // evaluation (you're in the final step of the program).
    }, []);

    const handleContinue = useCallback(async () => {
        const isLast = stepIdx === TOTAL_STEPS - 1;
        if (!isLast) {
            setStepIdx((prev) => prev + 1);
            return;
        }
        setSaving(true);
        try {
            await onSubmit({
                self_rating_technical: ratingTechnical,
                self_rating_communication: ratingCommunication,
                self_rating_leadership: ratingLeadership,
                highlights: highlights.trim(),
                challenges: challenges.trim(),
                next_12_months: nextTwelveMonths.trim(),
            });
            // Parent refreshes — wizard unmounts.
        } catch {
            setSaving(false);
        }
    }, [
        stepIdx, ratingTechnical, ratingCommunication, ratingLeadership,
        highlights, challenges, nextTwelveMonths, onSubmit,
    ]);

    const { title, subtitle, body } = renderStep({
        key: currentKey,
        ratingTechnical, setRatingTechnical,
        ratingCommunication, setRatingCommunication,
        ratingLeadership, setRatingLeadership,
        highlights, setHighlights,
        challenges, setChallenges,
        nextTwelveMonths, setNextTwelveMonths,
        disabled: saving,
    });

    return (
        <Card className="benchmark-wizard-card">
            <Card.Body>
                {saving ? (
                    <GeneratingCertificateOverlay />
                ) : (
                    <WizardShell
                        step={stepIdx + 1}
                        totalSteps={TOTAL_STEPS}
                        title={title}
                        subtitle={subtitle}
                        canContinue={canContinue}
                        onBack={stepIdx > 0 ? handleBack : null}
                        onContinue={handleContinue}
                        onExit={handleExit}
                        finalSparkle
                        continueLabel={stepIdx === TOTAL_STEPS - 1 ? 'Finish the program' : undefined}
                        busy={saving}
                    >
                        {body}
                    </WizardShell>
                )}
            </Card.Body>
        </Card>
    );
}

// ── Step renderer ──────────────────────────────────────────────────────

type RenderArgs = {
    key: StepKey;
    ratingTechnical: number;
    setRatingTechnical: (v: number) => void;
    ratingCommunication: number;
    setRatingCommunication: (v: number) => void;
    ratingLeadership: number;
    setRatingLeadership: (v: number) => void;
    highlights: string;
    setHighlights: (v: string) => void;
    challenges: string;
    setChallenges: (v: string) => void;
    nextTwelveMonths: string;
    setNextTwelveMonths: (v: string) => void;
    disabled: boolean;
};

function renderStep(args: RenderArgs): { title: string; subtitle?: string; body: ReactNode } {
    switch (args.key) {
        case 'rate_technical':
            return {
                title: 'Rate yourself on technical fluency — now',
                subtitle: '1 = still learning, 5 = confident',
                body: (
                    <SliderRating
                        value={args.ratingTechnical}
                        onChange={args.setRatingTechnical}
                        icon={<Wrench size={24} />}
                        min={1}
                        max={5}
                        leftLabel="Still learning"
                        rightLabel="Confident"
                        labelForValue={fiveScaleLabel}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'rate_communication':
            return {
                title: 'Rate yourself on communication — now',
                subtitle: '1 = still quiet, 5 = runs the room',
                body: (
                    <SliderRating
                        value={args.ratingCommunication}
                        onChange={args.setRatingCommunication}
                        icon={<Mic size={24} />}
                        min={1}
                        max={5}
                        leftLabel="Still quiet"
                        rightLabel="Runs the room"
                        labelForValue={fiveScaleLabel}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'rate_leadership':
            return {
                title: 'Rate yourself on leadership & decisiveness — now',
                subtitle: '1 = prefer to be told, 5 = make the call',
                body: (
                    <SliderRating
                        value={args.ratingLeadership}
                        onChange={args.setRatingLeadership}
                        icon={<HeartHandshake size={24} />}
                        min={1}
                        max={5}
                        leftLabel="Follower"
                        rightLabel="Decisive leader"
                        labelForValue={fiveScaleLabel}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'highlights':
            return {
                title: 'Highlights of the last 8 weeks',
                subtitle: 'What moments felt like genuine growth?',
                body: (
                    <TextAnswer
                        value={args.highlights}
                        onChange={args.setHighlights}
                        placeholder="e.g. The Week 4 permit exercise clicked after a decade of confusion. The Week 7 email I wrote to Nikhil actually worked…"
                        rows={5}
                        minLength={20}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'challenges':
            return {
                title: 'What was hardest?',
                subtitle: 'Be honest — this is the mirror session',
                body: (
                    <TextAnswer
                        value={args.challenges}
                        onChange={args.setChallenges}
                        placeholder="e.g. I still freeze in heated site meetings. The cost-plan exercise was the toughest week…"
                        rows={5}
                        minLength={20}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'next_12_months':
            return {
                title: 'Your 12-month plan',
                subtitle: 'What are your top 2–3 concrete goals for the year ahead?',
                body: (
                    <TextAnswer
                        value={args.nextTwelveMonths}
                        onChange={args.setNextTwelveMonths}
                        placeholder="e.g. Land an associate DM role by Q3, complete my first $20M feasibility, present at one UDIA event…"
                        rows={5}
                        minLength={20}
                        disabled={args.disabled}
                    />
                ),
            };
    }
}

function fiveScaleLabel(v: number): string {
    return ['Still learning', 'Developing', 'Solid', 'Strong', 'Elite'][v - 1] ?? '';
}

// ── Generating-certificate overlay ─────────────────────────────────────

function GeneratingCertificateOverlay() {
    return (
        <div className="bench-wiz__generating" role="status" aria-live="polite">
            <div className="bench-wiz__generating-halo" aria-hidden="true">
                <Sparkles size={36} />
            </div>
            <h3>Finalising your program</h3>
            <p>Recording your reflections and minting your certificate…</p>
        </div>
    );
}

// Unused icon import suppression — lucide is tree-shaken in prod.
void Star;
void Target;

export default EvaluationWizard;
